import * as React from 'react';
import { AlertCircle, CheckCircle2, Download, Loader2, RotateCw } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Icon } from './ui/Icon';

export type UpdateStatus =
  | 'checking'
  | 'up-to-date'
  | 'available'
  | 'downloading'
  | 'ready'
  | 'error';

export interface UpdateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: UpdateStatus;
  version?: string;
  currentVersion: string;
  progress?: number;
  error?: string;
  releaseNotes?: string | null;
  onUpdateNow: () => void;
  onRemindLater: () => void;
  onSkipVersion: () => void;
  onRetry: () => void;
}

/**
 * Must-answer update modal (Josh 2026-10-09): Update now / Remind me later /
 * Don't tell me again. Supersedes the 2026-09-25 "non-blocking, no
 * auto-opened modal" ruling — built on the kit Dialog (Radix focus trap,
 * Esc, outside-click) per docs/DESIGN.md's "one Modal" line, with the
 * available/downloading/ready states hardened into true must-answer (no X,
 * no outside-click dismiss, Esc = Remind me later).
 *
 * Copy law for this dialog (Josh 2026-10-20): a user asked whether "Restart
 * & update" would restart their COMPUTER. Every install-facing state now
 * says in plain words that only SpecForma closes and reopens, and the
 * primary button names the app ("Restart SpecForma"), never bare "Restart".
 */
export function UpdateDialog({
  open,
  onOpenChange,
  status,
  version,
  currentVersion,
  progress,
  error,
  onUpdateNow,
  onRemindLater,
  onSkipVersion,
  onRetry,
}: UpdateDialogProps) {
  const mustAnswer = status === 'available' || status === 'downloading' || status === 'ready';
  const primaryRef = React.useRef<HTMLButtonElement>(null);
  const v = version ? `v${version}` : 'a new version';
  const pct = progress ?? 0;

  const blockDismiss = (e: Event) => {
    if (mustAnswer) e.preventDefault();
  };
  const escDismiss = (e: Event) => {
    if (!mustAnswer) return;
    e.preventDefault();
    onRemindLater();
  };

  // One icon tile per state — hierarchy by shape and tint, never color alone.
  const tile: { icon: LucideIcon; tone: string; spin?: boolean } = {
    checking: { icon: Loader2, tone: 'bg-surface-alt text-muted', spin: true },
    'up-to-date': { icon: CheckCircle2, tone: 'bg-surface-alt text-success' },
    available: { icon: Download, tone: 'bg-primary-soft text-primary' },
    downloading: { icon: Loader2, tone: 'bg-primary-soft text-primary', spin: true },
    ready: { icon: RotateCw, tone: 'bg-primary-soft text-primary' },
    error: { icon: AlertCircle, tone: 'bg-surface-alt text-danger' },
  }[status];

  const title = {
    checking: 'Checking for updates…',
    'up-to-date': "You're up to date",
    available: `SpecForma ${v} is available`,
    downloading: `Downloading SpecForma ${v}…`,
    ready: `SpecForma ${v} is ready to install`,
    error: "Couldn't check for updates",
  }[status];

  const description = {
    checking: 'This takes a few seconds.',
    'up-to-date': `SpecForma v${currentVersion} is the latest version.`,
    available: `You're on v${currentVersion}. Updating takes about a minute. SpecForma closes and reopens on its own. Your computer won't restart.`,
    downloading: `${pct}% done. You can keep working. If you close this, the update installs itself the next time you quit SpecForma.`,
    ready:
      "Restart SpecForma to finish. It closes and reopens in about a minute. Your computer won't restart. If you'd rather wait, it installs itself the next time you quit SpecForma.",
    error: error || 'Check your internet connection and try again.',
  }[status];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md"
        hideClose={mustAnswer}
        onPointerDownOutside={blockDismiss}
        onInteractOutside={blockDismiss}
        onEscapeKeyDown={escDismiss}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          primaryRef.current?.focus();
        }}
      >
        <div className="flex items-start gap-4">
          <div
            aria-hidden="true"
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tile.tone}`}
          >
            <Icon icon={tile.icon} size="lg" className={tile.spin ? 'animate-spin' : undefined} />
          </div>
          <DialogHeader className="min-w-0 flex-1 pt-1">
            <DialogTitle className="leading-snug">{title}</DialogTitle>
            <DialogDescription className="leading-relaxed">{description}</DialogDescription>
          </DialogHeader>
        </div>

        {/* Release notes box removed (Josh 2026-10-20): the GitHub release body is
            empty, so the updater falls back to the tag commit's HTML ("Merge pull
            request #1…") — junk by construction. `releaseNotes` stays in the props
            so app.tsx is untouched; re-enable only with a real release-notes.md. */}

        {status === 'downloading' && (
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            className="h-1.5 w-full overflow-hidden rounded-full bg-border"
          >
            <div
              className="h-full rounded-full bg-primary transition-[width] duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}

        <DialogFooter>
          {status === 'checking' && (
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}

          {status === 'up-to-date' && (
            <Button ref={primaryRef} onClick={() => onOpenChange(false)}>
              OK
            </Button>
          )}

          {status === 'error' && (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              <Button ref={primaryRef} onClick={onRetry}>
                Try again
              </Button>
            </>
          )}

          {status === 'available' && (
            <>
              <Button variant="ghost" className="mr-auto text-muted" onClick={onSkipVersion}>
                Skip this version
              </Button>
              <Button variant="outline" onClick={onRemindLater}>
                Remind me later
              </Button>
              <Button ref={primaryRef} onClick={onUpdateNow}>
                Update now
              </Button>
            </>
          )}

          {status === 'downloading' && (
            <Button ref={primaryRef} variant="outline" onClick={onRemindLater}>
              Keep working
            </Button>
          )}

          {status === 'ready' && (
            <>
              <Button variant="ghost" className="mr-auto text-muted" onClick={onSkipVersion}>
                Skip this version
              </Button>
              <Button variant="outline" onClick={onRemindLater}>
                Remind me later
              </Button>
              <Button ref={primaryRef} onClick={onUpdateNow}>
                Restart SpecForma
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
